import Store from '@/store';
import { UserRole } from '@/types/UserRole';
import { UserPermission } from '@/types/UserPermissions';
import { User } from '@/types/User';
import { ComponentOptions } from 'vue';
import store from '@/store';

const userRoles = [
  UserRole.Admin,
  UserRole.Broker,
  UserRole.Carrier,
  UserRole.Client,
  UserRole.Market,
];

/**
 * Get the user's role.
 * The userRoles array is used to determine the user's role. It is ordered from most to least powerful.
 * If the user has multiple roles, the first role in the userRoles array that the user has will be returned.
 * If the client view is enabled, the user will be set as a client.
 */
export function GetUserRole(user?: User): UserRole {
  const roles = user?.roles;

  if (!roles) {
    console.error('User has no roles');
    return UserRole.None;
  }

  // set as client if the client view is enabled
  if (Store.state.clientViewState.isClientViewEnabled) {
    return UserRole.Client;
  }

  for (const role of userRoles) {
    if (roles.some((x) => x && x.endsWith(role))) {
      return role;
    }
  }

  return UserRole.None;
}

export function UserHasPermission(user: User | undefined, ...permissionsToCheck: UserPermission[]): boolean {
  if (IsUserAnAdmin(user)) {
    return true;
  }
  const permissions = store.state.clientViewState.isClientViewEnabled
    ? store.state.clientViewState.permissions
    : user?.permissions;

  if (!permissions) {
    return false;
  }
  for (const toCheck of permissionsToCheck) {
    let found = false;
    for (const userPerm of permissions) {
      if (userPerm && userPerm === toCheck) {
        found = true;
        break;
      }
    }
    if (!found) {
      return false;
    }
  }
  return true;
}

export function IsUserAnAdmin(user?: User): boolean {
  if (!user) {
    return false;
  }

  return GetUserRole(user) === UserRole.Admin;
}

export function IsUserABroker(user?: User): boolean {
  if (!user) {
    return false;
  }

  return GetUserRole(user) === UserRole.Broker;
}

export function IsMarketUser(user?: User): boolean {
  if (!user) {
    return false;
  }

  return GetUserRole(user) === UserRole.Market;
}

export function IsUserAClient(user?: User): boolean {
  if (!user) {
    return false;
  }

  return GetUserRole(user) === UserRole.Client;
}


export function GetAllowedAccountIds(user?: User): number[] {
  if (!user || !user.allowedAccounts) {
    return [];
  }

  if (Store.state.clientViewState.activeView?.allowedAccountIds) {
    return Store.state.clientViewState.activeView.allowedAccountIds;
  }

  return user.allowedAccounts.map((a) => a.accountId);
}

export function GetAllowedParentAccountIds(user?: User): number[] {
  if (!user || !user.allowedAccounts) {
    return [];
  }
  // if client view mode is enabled, return the allowed account ids for the active view
  // which will be used to compare against the parent account id
  if (Store.state.clientViewState.activeView?.allowedAccountIds) {
    return Store.state.clientViewState.activeView.allowedAccountIds;
  }

  return user.allowedAccounts.filter((a) => a.isParent).map((a) => a.accountId);
}

export function GetAllowedCarrierIds(user?: User): number[] {
  if (!user || !user.allowedCarrierIds) {
    return [];
  }
  return user?.allowedCarrierIds;
}

export function GetAllowedWholesalerIds(user?: User): number[] {
  if (!user || !user.allowedWholesalerIds) {
    return [];
  }
  return user?.allowedWholesalerIds;
}

export function AreAllRouteGroupsAllowed(user?: User): boolean {
  if (!user) {
    return false;
  }

  if (Store.state.clientViewState.isClientViewEnabled) {
    return false;
  }

  return user.allRouteGroupsAllowed;
}

export function AreAllAccountsAllowed(user?: User): boolean {
  if (!user) {
    return false;
  }

  if (Store.state.clientViewState.isClientViewEnabled) {
    return false;
  }

  return user.allAccountsAllowed;
}

export function tenantName(): string {
  const tenantName = Store.state.user?.tenantName;
  return (tenantName && tenantName.trim()) || '';
}

/**
 * Check if the user has access to the route group. Ex. Tenant 3 has an option that is globally disabled
 * for all users in the Tenant.
 *
 * @param routeGroup
 * @constructor
 */
export function HasAccessToRouteGroup(routeGroup: string): boolean {
  const user = store.state.user;
  if (!user) {
    return false;
  }

  if (AreAllRouteGroupsAllowed(user)) {
    return true;
  }

  return (user.allowedRouteGroups || []).includes(routeGroup);
}

export function HasAccess(permission: string): boolean {
  const user = store.state.user;

  if (!user) return false;
  if (IsUserAnAdmin(user)) return true;

  if (IsUserAClient(user)) {
    return user.permissions.includes(permission);
  }

  // todo: expand for other roles as needed

  return false;
}

export const AuthMixin: ComponentOptions = {
  methods: {
    userHasPermissions(...permissions: string[]): boolean {
      const user = Store.state.user;
      if (!user) {
        return false;
      }
      if (permissions.length === 0) {
        return true;
      }
      for (const permission of permissions) {
        if (!user.permissions.includes(permission)) {
          return false;
        }
      }
      return true;
    },
  },
};
